import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import Box from "components/common/Box";
import Stack from "components/common/Stack";
import Text from "components/common/Text";
import Button from "components/common/Button";
import IconButton from "components/common/IconButton";
import AllInOneFooter from "components/allInOneDashboard/AllInOneFooter";
import PhcPricingSection from "components/Product-Info-Pages/Phc/PhcPricingSection";
import { UNIFIED_DASHBOARD_PATH } from "components/allInOneDashboard/constants/productRouteMap";
import { config } from "config/index";
import { PHCOnboardingActions } from "stores/PHC/PHCOnboarding/PHCOnboardingAction";
import { localStore } from "stores/localStorage";
import { thirdpartyApis } from "stores/thirdparty/thirdpartyApis";
import { thirdpartyParsers } from "stores/thirdparty/thirdpartyParsers";

const ONBOARDING_PATH = "/phc/onboarding/overview";

const ACCENT = "#AE4D4E";
const FACT_DOT = "#C13838";
const YEAR_CARD_BG = "#FC9191";
const HERO_GRADIENT_WEB = "linear-gradient(162deg, #231919 1.5%, #F34848 98%)";
const HERO_GRADIENT_MOBILE = "linear-gradient(136deg, #120707 19%, #FB3E3E 98.5%)";
const CTA_GRADIENT = "linear-gradient(270deg, #D03535 0%, #5C2627 100%)";
const CTA_GRADIENT_MOBILE = "linear-gradient(283deg, #D83535 0.6%, #552626 99%)";
const FOR_YOU_GRADIENT = "linear-gradient(134deg, #221919 22%, #ED6767 99%)";
const FEATURE_BORDER = "#E5E7EB";
const FOR_YOU_BORDER = "#F5D0D0";
const FACT_BORDER = "#E2E8F0";

const img = (name) => `/images/phc-product-info/${encodeURIComponent(name)}`;

const ICONS = {
  hero: img("phc.png"),
  frame: img("Frame.svg"),
  check: img("icon.svg"),
  process: img("process 1.svg"),
  verified: img("verified 1.svg"),
  identification: img("identification 1.svg"),
  detection: img("detection 1.svg"),
  tracking: img("tracking 1.svg"),
};

const FEATURES = [
  {
    icon: ICONS.process,
    isSteps: true,
    text: "3 Steps: Upload your portfolio or connect your broker Analyze your portfolio Get your personalized report",
  },
  {
    icon: ICONS.verified,
    text: "Comprehensive portfolio health analysis in just a few steps",
  },
  {
    icon: ICONS.identification,
    text: "Identify concentration and diversification risks across your holdings",
  },
  {
    icon: ICONS.detection,
    text: "Detect portfolio weaknesses that may impact long-term performance",
  },
  {
    icon: ICONS.tracking,
    text: "Easy to understand insights presented through intuitive visuals",
    fullWidth: true,
  },
];

const FOR_YOU_CARDS = [
  { label: "Beyond Returns", description: "Want to understand your portfolio beyond returns" },
  { label: "Existing Holdings", description: "For existing holdings only" },
  { label: "Hidden Risks", description: "Need to identify, hidden portfolio risks" },
  { label: "Improve", description: "Want to improve Diversification" },
  { label: "Insights", description: "Prefer data backed portfolio insights" },
  { label: "Recommendations", description: "Want recommendations to build insights" },
];

const QUICK_FACTS = [
  { label: "Assessment", value: "Free" },
  { label: "Steps", value: "3 Step Process" },
  { label: "Analysis", value: "AI Driven" },
  { label: "Analysis On", value: "Existing Portfolio" },
];

const Bold = ({ children }) => (
  <Box component="span" sx={{ fontWeight: 600 }}>
    {children}
  </Box>
);

const MOBILE_ABOUT_ITEMS = [
  {
    icon: ICONS.frame,
    text: (
      <>
        <Bold>AI engine live</Bold> in Indian Market since <Bold>2016</Bold>
      </>
    ),
  },
  {
    icon: ICONS.verified,
    text: "Comprehensive portfolio health analysis in just a few steps.",
  },
  {
    icon: ICONS.process,
    isSteps: true,
  },
  {
    icon: ICONS.identification,
    text: "Identify concentration and diversification risks across your holdings.",
  },
  {
    icon: ICONS.detection,
    text: "Detect portfolio weaknesses that may impact long-term performance.",
  },
  {
    icon: ICONS.tracking,
    text: "Easy to understand insights presented through intuitive visuals",
  },
];

const MOBILE_STEPS = [
  "Upload your portfolio or connect your broker",
  "Analyze your portfolio",
  "Get your personalized report",
];

const MOBILE_FOR_YOU = [
  "Want to understand your portfolio beyond returns.",
  "For existing holdings only",
  "Need to identify hidden portfolio risks.",
  "Want to improve diversification.",
  "Prefer data-backed portfolio insights.",
  "Want recommendations to build a healthier portfolio.",
];

const MOBILE_QUICK_FACTS = [
  { label: "Assessment", value: "Free" },
  { label: "Steps", value: "3 Step Process" },
  { label: "Analysis", value: "AI Driven" },
  { label: "Analysis On", value: "Existing Portfolio" },
];

const ABOUT_COLLAPSED_HEIGHT = 141;

const pagePad = {
  px: { xs: 2.5, md: 7.5 },
  maxWidth: 1320,
  mx: "auto",
  width: "100%",
};

const FeatureCard = ({ icon, text, isSteps, fullWidth }) => (
  <Stack
    spacing={1.5}
    sx={{
      p: 4,
      height: "100%",
      borderRadius: "20px",
      bgcolor: "#fff",
      border: `1px solid ${FEATURE_BORDER}`,
      boxShadow: "0 6px 9px rgba(0,0,0,0.05)",
      gridColumn: fullWidth ? "1 / -1" : "auto",
    }}
  >
    <Box component="img" src={icon} alt="" sx={{ width: 32, height: 32 }} />
    {isSteps ? (
      <Box>
        <Text sx={{ fontSize: 18, fontWeight: 600, color: "#111827", lineHeight: "21px", mb: 1 }}>
          3 Steps:
        </Text>
        <Box component="ul" sx={{ m: 0, pl: 2.75, color: "#111827" }}>
          {[
            "Upload your portfolio or connect your broker",
            "Analyze your portfolio",
            "Get your personalized report",
          ].map((item) => (
            <Box
              component="li"
              key={item}
              sx={{ fontSize: 18, fontWeight: 500, lineHeight: "21px", mb: 1, "&:last-child": { mb: 0 } }}
            >
              {item}
            </Box>
          ))}
        </Box>
      </Box>
    ) : (
      <Text sx={{ fontSize: 18, fontWeight: 500, color: "#111827", lineHeight: 1.45 }}>
        {text}
      </Text>
    )}
  </Stack>
);

const ChecklistCard = ({ label, description }) => (
  <Stack
    spacing={1.5}
    sx={{
      p: 3,
      height: "100%",
      borderRadius: "12px",
      bgcolor: "#fff",
      border: `1px solid ${FOR_YOU_BORDER}`,
      boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
      position: "relative",
      overflow: "hidden",
    }}
  >
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: 3,
        bgcolor: ACCENT,
      }}
    />
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Box
        component="img"
        src={ICONS.check}
        alt=""
        sx={{ width: 36, height: 36, flexShrink: 0 }}
      />
      <Text sx={{ fontSize: 15, fontWeight: 600, color: "#374151", lineHeight: "20px" }}>
        {label}
      </Text>
    </Stack>
    <Text sx={{ fontSize: 13, color: "#313131", lineHeight: "20px" }}>{description}</Text>
  </Stack>
);

const FactCard = ({ label, value }) => (
  <Stack
    spacing={1.25}
    sx={{
      p: 3,
      borderRadius: "16px",
      bgcolor: "#fff",
      border: `1px solid ${FACT_BORDER}`,
      boxShadow: "0 4px 12px rgba(0,0,0,0.04)",
    }}
  >
    <Stack direction="row" spacing={1} alignItems="center">
      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: FACT_DOT }} />
      <Text sx={{ fontSize: 18, color: "#6B7280" }}>{label}</Text>
    </Stack>
    <Text sx={{ fontSize: 24, fontWeight: 700, color: "#111827" }}>{value}</Text>
  </Stack>
);

const MobileAboutRow = ({ icon, text, isSteps }) => (
  <Stack direction="row" spacing={1.5} alignItems="flex-start">
    <Box
      component="img"
      src={icon}
      alt=""
      sx={{ width: 20, height: 20, flexShrink: 0, mt: "2px" }}
    />
    {isSteps ? (
      <Box sx={{ color: "#475569", fontSize: 14, lineHeight: "21px" }}>
        <Text sx={{ fontSize: 14, fontWeight: 500, color: "#475569", lineHeight: "21px", mb: 0 }}>
          3 Steps:
        </Text>
        <Box
          component="ul"
          sx={{
            m: 0,
            pl: "21px",
            listStyleType: "disc",
            "& li": {
              fontSize: 14,
              lineHeight: "21px",
              mb: "8px",
              "&:last-child": { mb: 0 },
            },
          }}
        >
          {MOBILE_STEPS.map((step) => (
            <Box component="li" key={step}>
              {step}
            </Box>
          ))}
        </Box>
      </Box>
    ) : (
      <Text sx={{ fontSize: 14, color: "#475569", lineHeight: "21px" }}>{text}</Text>
    )}
  </Stack>
);

const MobileForYouRow = ({ text }) => (
  <Stack
    direction="row"
    spacing={2}
    alignItems="center"
    sx={{
      p: 2,
      minHeight: 63,
      borderRadius: "16px",
      bgcolor: "rgba(255,255,255,0.12)",
      border: "1px solid rgba(255,255,255,0.12)",
      boxShadow: "0 10px 12px rgba(0,0,0,0.1)",
      backdropFilter: "blur(7px)",
    }}
  >
    <Box
      sx={{
        width: 24,
        height: 24,
        flexShrink: 0,
        display: "grid",
        placeItems: "center",
      }}
    >
      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: FACT_DOT }} />
    </Box>
    <Text sx={{ fontSize: 14, fontWeight: 500, color: "#fff", lineHeight: 1.35 }}>
      {text}
    </Text>
  </Stack>
);

const MobileFactTile = ({ label, value }) => (
  <Stack
    spacing={1.25}
    sx={{
      flex: 1,
      minWidth: 0,
      minHeight: 106,
      p: 2,
      borderRadius: "16px",
      bgcolor: "#fff",
      border: `1px solid ${FACT_BORDER}`,
      boxShadow: "0 4px 6px rgba(0,0,0,0.04)",
    }}
  >
    <Stack direction="row" spacing={1.25} alignItems="center">
      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: FACT_DOT }} />
      <Text sx={{ fontSize: 12, color: "#000", lineHeight: "16px" }}>{label}</Text>
    </Stack>
    <Text sx={{ fontSize: 16, fontWeight: 700, color: "#0F172A", lineHeight: "24px" }}>
      {value}
    </Text>
  </Stack>
);

const PhcProductInfo = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [aboutExpanded, setAboutExpanded] = useState(false);
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    dispatch(PHCOnboardingActions.getOnboardingData());
    thirdpartyApis
      .getSubscriptionPlans({ productCode: config.productCodes.PHC })
      .then((res) => setPlans(thirdpartyParsers.subscriptionPlans(res) || []))
      .catch(() => setPlans([]));
  }, [dispatch]);

  const handleStart = () => {
    localStore.setActiveProduct(config.productCodes.PHC);
    history.push(ONBOARDING_PATH);
  };

  const handleBack = () => {
    history.push(UNIFIED_DASHBOARD_PATH);
  };

  const middleFeatures = FEATURES.slice(1, -1);
  const fullWidthFeature = FEATURES[FEATURES.length - 1];

  return (
    <Box sx={{ bgcolor: "#fff" }}>
      {/* Hero */}
      <Box
        sx={{
          background: {
            xs: HERO_GRADIENT_MOBILE,
            md: HERO_GRADIENT_WEB,
          },
          px: { xs: 3, md: 7.5 },
          pt: { xs: 2.5, md: 4 },
          pb: { xs: 0, md: 10 },
          overflow: "hidden",
          borderRadius: { xs: "0 0 24px 24px", md: 0 },
          boxShadow: {
            xs: "0 12px 28px -10px rgba(99,23,176,0.1)",
            md: "none",
          },
        }}
      >
        <Box sx={{ maxWidth: 1320, mx: "auto" }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ mb: { xs: 2.5, md: 6 } }}
          >
            <IconButton
              onClick={handleBack}
              aria-label="Back to Explore"
              sx={{
                width: { xs: 42, md: 42 },
                height: { xs: 42, md: 42 },
                borderRadius: "12px",
                bgcolor: "rgba(255,255,255,0.2)",
                border: "1px solid #C3C3C3",
                boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
                p: 0,
                "&:hover": { bgcolor: "rgba(255,255,255,0.35)" },
              }}
            >
              <ArrowBackRoundedIcon sx={{ fontSize: { xs: 20, md: 24 }, color: "#fff" }} />
            </IconButton>
            <Stack spacing={{ xs: 0.25, md: 1.1 }}>
              <Text
                sx={{
                  display: { xs: "block", md: "none" },
                  fontSize: 18,
                  fontWeight: 800,
                  color: "#fff",
                  lineHeight: "24px",
                }}
              >
                Jarvis Portfolio Health Check
              </Text>
              <Text
                sx={{
                  display: { xs: "none", md: "block" },
                  fontSize: 32,
                  fontWeight: 800,
                  color: "#fff",
                  lineHeight: "26px",
                }}
              >
                Jarvis PHC
              </Text>
              <Text
                sx={{
                  fontSize: 14,
                  fontWeight: 400,
                  color: "rgba(255,255,255,0.7)",
                  lineHeight: "21px",
                }}
              >
                Investing powered by AI
              </Text>
            </Stack>
          </Stack>

          {/* Desktop hero */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={4}
            sx={{ display: { xs: "none", md: "flex" } }}
          >
            <Stack spacing={3} sx={{ maxWidth: 620 }}>
              <Text
                sx={{
                  fontSize: 56,
                  fontWeight: 800,
                  color: "#fff",
                  lineHeight: 1.15,
                }}
              >
                Check your Portfolio. Improve It.
              </Text>
              <Text
                sx={{
                  fontSize: 20,
                  color: "rgba(255,255,255,0.75)",
                  lineHeight: 1.5,
                }}
              >
                Get an AI-powered health check of your portfolio to uncover hidden risks,
                identify opportunities, and make more informed investment decisions.
              </Text>
              <Button
                onClick={handleStart}
                endIcon={<ArrowForwardRoundedIcon />}
                sx={{
                  alignSelf: "flex-start",
                  mt: 5,
                  px: 3.5,
                  py: 1.5,
                  borderRadius: "14px",
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: 15,
                  color: "#fff",
                  background: CTA_GRADIENT,
                  border: "1px solid rgba(255,255,255,0.3)",
                  "&:hover": { background: CTA_GRADIENT, opacity: 0.95 },
                }}
              >
                Upload Portfolio
              </Button>
            </Stack>
            <Box
              component="img"
              src={ICONS.hero}
              alt="Jarvis PHC"
              sx={{ width: 360, maxWidth: "100%", objectFit: "contain" }}
            />
          </Stack>

          {/* Mobile hero */}
          <Stack spacing={1.25} sx={{ display: { xs: "flex", md: "none" } }}>
            <Text
              sx={{
                fontSize: 30,
                fontWeight: 800,
                color: "#fff",
                lineHeight: "38px",
              }}
            >
              Check your Portfolio. Improve It.
            </Text>
            <Text
              sx={{
                fontSize: 14,
                color: "rgba(255,255,255,0.72)",
                lineHeight: "21px",
              }}
            >
              Get an AI-powered health check of your portfolio to uncover hidden risks,
              identify opportunities, and make more informed investment decisions.
            </Text>
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "space-between",
                gap: 1.5,
                mt: 5,
                minHeight: 200,
                position: "relative",
              }}
            >
              <Box
                component="img"
                src={ICONS.hero}
                alt="Jarvis PHC"
                sx={{
                  width: 214,
                  height: 248,
                  maxWidth: "58%",
                  objectFit: "cover",
                  objectPosition: "center top",
                  alignSelf: "flex-end",
                  ml: "-33px",
                  mb: "-24px",
                }}
              />
              <Button
                onClick={handleStart}
                endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: 24 }} />}
                sx={{
                  flexShrink: 0,
                  mb: 2.5,
                  height: 48,
                  px: 2,
                  py: 1.75,
                  borderRadius: "14px",
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: 14,
                  color: "#fff",
                  whiteSpace: "nowrap",
                  background: CTA_GRADIENT_MOBILE,
                  border: "1.5px solid rgba(255,255,255,0.3)",
                  boxShadow: "0 10px 12px rgba(142,62,62,0.2)",
                  "&:hover": { background: CTA_GRADIENT_MOBILE, opacity: 0.95 },
                }}
              >
                Upload Portfolio
              </Button>
            </Box>
          </Stack>
        </Box>
      </Box>

      {/* About PHC — desktop */}
      <Box sx={{ ...pagePad, py: 7, display: { xs: "none", md: "block" } }}>
        <Text
          sx={{
            textAlign: "center",
            fontSize: 48,
            fontWeight: 700,
            color: "#111827",
            letterSpacing: "-0.02em",
            mb: 5,
          }}
        >
          About PHC
        </Text>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "2fr 1fr",
            gap: 2,
            mb: 2,
          }}
        >
          <Box
            sx={{
              p: 4,
              borderRadius: "20px",
              bgcolor: YEAR_CARD_BG,
              boxShadow: "0 8px 16px rgba(0,0,0,0.08)",
              minHeight: 200,
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box component="img" src={ICONS.frame} alt="" sx={{ width: 28, height: 28 }} />
              <Text
                sx={{
                  bgcolor: "#fff",
                  px: 1.5,
                  py: 0.5,
                  borderRadius: "12px",
                  fontSize: 11,
                  fontWeight: 500,
                  color: ACCENT,
                }}
              >
                Currently AI under reinforcement stage
              </Text>
            </Stack>
            <Text sx={{ mt: 1.5, fontSize: 80, fontWeight: 700, color: "#fff", lineHeight: 1 }}>
              2016
            </Text>
            <Text sx={{ mt: 1, fontSize: 20, fontWeight: 500, color: "rgba(255,255,255,0.9)" }}>
              AI Engine live in Indian Market since
            </Text>
          </Box>
          <FeatureCard {...FEATURES[0]} />
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 2,
            mb: 2,
          }}
        >
          {middleFeatures.map((item) => (
            <FeatureCard key={item.text} {...item} />
          ))}
        </Box>

        <FeatureCard {...fullWidthFeature} />
      </Box>

      {/* About PHC — mobile */}
      <Box sx={{ px: 3, pt: 2.5, pb: 1.25, display: { xs: "block", md: "none" } }}>
        <Stack
          spacing={2}
          sx={{
            p: 2.5,
            borderRadius: "20px",
            bgcolor: "#fff",
            border: "1px solid #E2E8F0",
            boxShadow: "0 4px 6px rgba(0,0,0,0.04)",
          }}
        >
          <Text sx={{ fontSize: 18, fontWeight: 700, color: "#0F172A", lineHeight: "24px" }}>
            About PHC
          </Text>
          <Box sx={{ borderBottom: "1px solid #E2E8F0" }} />
          <Box
            sx={{
              maxHeight: aboutExpanded ? "none" : ABOUT_COLLAPSED_HEIGHT,
              overflow: "hidden",
            }}
          >
            <Stack spacing={2.5}>
              {MOBILE_ABOUT_ITEMS.map((item, index) => (
                <MobileAboutRow key={`about-${index}`} {...item} />
              ))}
            </Stack>
          </Box>
          <Box
            component="button"
            type="button"
            onClick={() => setAboutExpanded((v) => !v)}
            sx={{
              border: 0,
              bgcolor: "transparent",
              cursor: "pointer",
              p: 0,
              alignSelf: "flex-start",
            }}
          >
            <Text sx={{ fontSize: 14, fontWeight: 500, color: "#2977EC", lineHeight: "24px" }}>
              {aboutExpanded ? "read less" : "read more"}
            </Text>
          </Box>
        </Stack>
      </Box>

      {/* Is This For You — desktop */}
      <Box
        sx={{
          display: { xs: "none", md: "block" },
          py: 8,
          px: 7.5,
          bgcolor: "#fff",
          backgroundImage: `
            linear-gradient(rgba(174,77,78,0.06) 1px, transparent 1px),
            linear-gradient(90deg, rgba(174,77,78,0.06) 1px, transparent 1px)
          `,
          backgroundSize: "200px 160px",
        }}
      >
        <Stack
          direction="row"
          spacing={6}
          alignItems="center"
          sx={{ maxWidth: 1320, mx: "auto" }}
        >
          <Stack spacing={2.5} sx={{ flex: "0 1 420px" }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                border: `1.5px solid ${ACCENT}`,
                display: "grid",
                placeItems: "center",
                bgcolor: "#fff",
                fontSize: 36,
                fontWeight: 800,
                color: ACCENT,
              }}
            >
              ?
            </Box>
            <Text sx={{ fontSize: 48, fontWeight: 800, color: "#1A1A1A", lineHeight: 1.1 }}>
              Is This For You?
            </Text>
            <Text sx={{ fontSize: 16, color: "#6B7280", lineHeight: 1.55 }}>
              A quick checklist to help you decide if our AI-driven portfolio management is the
              right fit for your financial goals.
            </Text>
          </Stack>

          <Box
            sx={{
              flex: 1,
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 2,
              width: "100%",
            }}
          >
            {FOR_YOU_CARDS.map((card) => (
              <ChecklistCard key={card.label} {...card} />
            ))}
          </Box>
        </Stack>
      </Box>

      {/* Is This For You — mobile */}
      <Box sx={{ px: 3, py: 1.25, display: { xs: "block", md: "none" } }}>
        <Stack
          spacing={2.5}
          sx={{
            p: 3,
            borderRadius: "24px",
            overflow: "hidden",
            position: "relative",
            border: "1px solid rgba(255,255,255,0.15)",
            background: FOR_YOU_GRADIENT,
            boxShadow:
              "0 0 18px rgba(34,211,238,0.1), 0 10px 24px -10px rgba(29,78,216,0.2), 0 18px 40px -12px rgba(0,0,0,0.15)",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: -111,
              right: -91,
              width: 260,
              height: 260,
              borderRadius: "50%",
              background:
                "radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 70%)",
              pointerEvents: "none",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: -121,
              right: 39,
              width: 220,
              height: 220,
              borderRadius: "50%",
              background:
                "radial-gradient(circle, rgba(255,255,255,0.16) 0%, transparent 70%)",
              pointerEvents: "none",
            }}
          />
          <Stack spacing={1.25} sx={{ position: "relative" }}>
            <Text sx={{ fontSize: 18, fontWeight: 700, color: "#E6E6E6", lineHeight: "24px" }}>
              Is This For You?
            </Text>
            <Text sx={{ fontSize: 13, color: "rgba(255,255,255,0.8)", lineHeight: "18px" }}>
              A quick checklist to help you decide if this is the right fit.
            </Text>
          </Stack>
          <Box sx={{ borderBottom: "1px solid rgba(255,255,255,0.2)", position: "relative" }} />
          <Stack spacing={1.5} sx={{ position: "relative" }}>
            {MOBILE_FOR_YOU.map((text) => (
              <MobileForYouRow key={text} text={text} />
            ))}
          </Stack>
        </Stack>
      </Box>

      {/* Quick Facts — desktop */}
      <Box sx={{ ...pagePad, py: 7, display: { xs: "none", md: "block" } }}>
        <Text
          sx={{
            textAlign: "center",
            fontSize: 36,
            fontWeight: 700,
            color: "#111827",
            mb: 4,
          }}
        >
          Quick Facts
        </Text>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 2,
          }}
        >
          {QUICK_FACTS.map((fact) => (
            <FactCard key={`${fact.label}-${fact.value}`} {...fact} />
          ))}
        </Box>
      </Box>

      {/* Quick Facts — mobile 2-col */}
      <Box sx={{ px: 3, py: 1.25, display: { xs: "block", md: "none" } }}>
        <Stack
          spacing={2.5}
          sx={{
            p: 3,
            borderRadius: "20px",
            bgcolor: "#fff",
            border: "1px solid #E2E8F0",
            boxShadow: "0 4px 6px rgba(0,0,0,0.04)",
          }}
        >
          <Text sx={{ fontSize: 18, fontWeight: 700, color: "#0F172A", lineHeight: "26px" }}>
            Quick Facts
          </Text>
          <Box sx={{ borderBottom: "1px solid #E2E8F0" }} />
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
            {MOBILE_QUICK_FACTS.map((fact) => (
              <MobileFactTile key={`${fact.label}-${fact.value}`} {...fact} />
            ))}
          </Box>
        </Stack>
      </Box>

      {/* Pricing */}
      <Box
        sx={{
          px: { xs: 2, md: 7.5 },
          py: { xs: 1.25, md: 10 },
          bgcolor: { xs: "#fff", md: "#F8FAFC" },
          maxWidth: "100%",
        }}
      >
        <Box sx={{ maxWidth: 1320, mx: "auto", width: "100%" }}>
          <Box sx={{ display: { xs: "none", md: "block" } }}>
            <Text
              sx={{
                textAlign: "center",
                fontSize: 48,
                fontWeight: 800,
                color: "#0F172A",
                mb: 1.5,
              }}
            >
              Pricing
            </Text>
            {/* <Text
              sx={{
                textAlign: "center",
                fontSize: 20,
                color: "#475569",
                mb: 5,
              }}
            >
              Choose the plan that fits your investing style
            </Text> */}
          </Box>
          <PhcPricingSection plans={plans} onUnlock={handleStart} />
        </Box>
      </Box>

      <AllInOneFooter />
    </Box>
  );
};

export default PhcProductInfo;
